import { useContext, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BotContext } from "@/pages/cbb-builder/store";
import AddTextStep from "./AddTextStep";
import ConnectAppsStep from "./ConnectAppsStep";
import OnboardingTrainedLinksTable from "./OnboardingTrainedLinksTable";
import UploadFilesStep from "./UploadFilesStep";
import { trainBotTabs, type TrainBotTabId } from "./trainBotTabs";
import Button from "./Button";
import Input from "./Input";
import { WEBSITE_URL_FIELD } from "../constants";
import type { CustomStepProps } from "../types";

const tabPanelMotion = {
  initial: { opacity: 0, y: 14, scale: 0.985, filter: "blur(4px)" },
  animate: { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" },
  exit: { opacity: 0, y: -10, scale: 0.99, filter: "blur(3px)" },
  transition: { duration: 0.24, ease: [0.22, 1, 0.36, 1] },
} as const;

const BACKGROUND_TRAINING_STATUSES = new Set(["In Progress", "Re-syncing"]);

const BackgroundTrainingBanner = ({
  message,
  onClose,
}: {
  message: string;
  onClose: () => void;
}) => (
  <motion.div
    className="my-[20px] flex items-center justify-between gap-[12px] rounded-[8px] bg-[#EBF2FF] p-[12px]"
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -6 }}
    transition={{ duration: 0.2, ease: "easeOut" }}
  >
    <div className="flex items-center gap-[5px]">
      <span className="shrink-0">
        <div className="training-banner-loader"></div>
      </span>
      <p className="text-[12px] font-normal leading-[110%] text-[#444]">
        {message}
      </p>
    </div>

    <button
      type="button"
      aria-label="Dismiss training message"
      className="shrink-0 rounded-[4px] p-[2px] text-[#6D6D6D] transition-colors hover:bg-[#dce7ff]"
      onClick={onClose}
    >
      <TrainingBannerCloseIcon />
    </button>
  </motion.div>
);

const TrainBotStep = ({
  values,
  inline = false,
  restartPolling,
  handleFieldChange,
  fetchWebsiteContent,
  isWebsiteImportLoading = false,
  websiteUrlError = "",
}: CustomStepProps) => {
  const [store] = useContext(BotContext);
  const [activeTab, setActiveTab] = useState<TrainBotTabId>("websitePages");
  const [iconAnimationTrigger, setIconAnimationTrigger] =
    useState<TrainBotTabId | null>(null);
  const [isTrainingBannerDismissed, setIsTrainingBannerDismissed] =
    useState(false);
  const hasWebsiteLinks = (store.botData?.aiSource?.urls ?? []).length > 0;

  const isBackgroundTrainingActive = useMemo(() => {
    const aiSource = store.botData?.aiSource ?? {};

    const urls = aiSource.urls ?? [];
    const documents = aiSource.documents ?? [];
    const connectedSources = [
      ...(aiSource.googlepicker ?? []),
      ...(aiSource.notion ?? []),
      ...(aiSource.confluence ?? []),
      ...(aiSource.salesforce ?? []),
    ];

    return [...urls, ...documents, ...connectedSources].some((item: any) =>
      BACKGROUND_TRAINING_STATUSES.has(item?.status ?? ""),
    );
  }, [store.botData?.aiSource]);

  const backgroundTrainingMessage = useMemo(() => {
    const aiSource = store.botData?.aiSource ?? {};
    const hasLinksTraining = (aiSource.urls ?? []).some((item: any) =>
      BACKGROUND_TRAINING_STATUSES.has(item?.status ?? ""),
    );
    const hasDocumentTraining = (aiSource.documents ?? []).some((item: any) =>
      BACKGROUND_TRAINING_STATUSES.has(item?.status ?? ""),
    );
    const hasConnectedSourceTraining = [
      ...(aiSource.googlepicker ?? []),
      ...(aiSource.notion ?? []),
      ...(aiSource.confluence ?? []),
      ...(aiSource.salesforce ?? []),
    ].some((item: any) => BACKGROUND_TRAINING_STATUSES.has(item?.status ?? ""));

    if (
      (hasLinksTraining && hasDocumentTraining) ||
      (hasLinksTraining && hasConnectedSourceTraining) ||
      (hasDocumentTraining && hasConnectedSourceTraining)
    ) {
      return "Your links and documents are training in the background. You can explore other options while you wait.";
    }

    if (hasDocumentTraining || hasConnectedSourceTraining) {
      return "Your documents are training in the background. You can explore other options while you wait.";
    }

    return "Your links are training in the background. You can explore other options while you wait.";
  }, [store.botData?.aiSource]);

  useEffect(() => {
    if (isBackgroundTrainingActive) {
      setIsTrainingBannerDismissed(false);
    }
  }, [isBackgroundTrainingActive]);

  const handleTabClick = (tabId: TrainBotTabId) => {
    setActiveTab(tabId);
    setIconAnimationTrigger(tabId);
    window.setTimeout(() => setIconAnimationTrigger(null), 300);
  };

  return (
    <div className="flex w-full flex-col gap-[5px]">
      <div
        className={`sticky z-[6] w-full bg-white pb-[20px] pt-[20px] max-[1200px]:top-[-9px] ${inline ? "top-[89px]" : "top-[56px]"}`}
      >
        <div className="flex h-[47px] items-center justify-between rounded-[12px] bg-white px-[5px] shadow-[0_0px_24px_0px_rgba(0,0,0,0.08)] max-[1200px]:h-[42px] max-[1200px]:px-[4px]">
          <div className="grid w-full grid-cols-4 gap-[4px] max-[1200px]:gap-[5px]">
            {trainBotTabs.map((tab) => {
              const isActive = activeTab === tab.id;

              return (
                <motion.button
                  key={tab.id}
                  className={`relative flex h-[37px] items-center justify-center gap-[7px] truncate rounded-[8px] px-[10px] text-[14px] font-normal leading-[120%] transition-[color,transform] duration-200 max-[1200px]:h-[34px] max-[1200px]:gap-[4px] max-[1200px]:px-[6px] max-[1200px]:text-[12px] ${
                    isActive
                      ? "font-medium text-[#444444] shadow-[0_0_3px_0_rgba(0,0,0,0.13)]"
                      : "text-[#666666cc] hover:bg-[#fafafa] hover:text-[#444]"
                  }`}
                  type="button"
                  onClick={() => handleTabClick(tab.id)}
                  whileTap={{ scale: 0.985 }}
                >
                  {isActive && (
                    <motion.div
                      className="absolute inset-0 z-0 rounded-[8px] bg-[#F9F9F9] shadow-[0_6px_16px_rgba(0,0,0,0.08)]"
                      layoutId="train-bot-tab-bg"
                      transition={{
                        type: "spring",
                        stiffness: 360,
                        damping: 32,
                        mass: 0.7,
                      }}
                    />
                  )}
                  <span className="relative z-10 flex items-center gap-[7px] max-[1200px]:gap-[4px]">
                    <motion.span
                      className="flex-shrink-0 max-[1200px]:scale-[0.85]"
                      initial={{ rotate: 0, scale: 1 }}
                      animate={
                        iconAnimationTrigger === tab.id
                          ? { scale: [1, 1.08, 0.98, 1], rotate: [0, -8, 8, 0] }
                          : { rotate: 0, scale: 1 }
                      }
                      transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {tab.icon}
                    </motion.span>
                    <motion.span
                      className="truncate max-[1200px]:text-[12px] max-[1200px]:tracking-[-0.01em]"
                      animate={{
                        opacity: isActive ? 1 : 0.84,
                        y: isActive ? 0 : 0.5,
                      }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                    >
                      {tab.label}
                    </motion.span>
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      <motion.div className="w-full" {...tabPanelMotion}>
        <div
          className={`w-full ${
            activeTab === "websitePages" ? "flex flex-col gap-[10px]" : "hidden"
          }`}
        >
          <div className="flex flex-col gap-[10px]">
            <label className="flex flex-col gap-[10px] text-[14px] font-medium leading-[110%] text-[#444]">
              {WEBSITE_URL_FIELD.label}
              <div className="relative [&_input]:!pr-[132px]">
                <Input
                  currentField={WEBSITE_URL_FIELD}
                  values={values}
                  handleFieldChange={handleFieldChange!}
                  error={websiteUrlError}
                  onEnterPress={fetchWebsiteContent}
                />
                <Button
                  action={{
                    id: "fetchWebsiteContent",
                    label: "Import Content",
                    variant: "primary",
                  }}
                  isCurrentFieldInvalid={!values.websiteUrl.trim()}
                  handleActionClick={() => fetchWebsiteContent?.()}
                  stepId={2}
                  isLoading={isWebsiteImportLoading}
                  className="absolute right-[8.5px] top-[8.5px] !h-[32px] !rounded-[10px] !px-[12px] !py-[8px] !text-[12px] max-[1280px]:top-[7.5px]"
                />
              </div>
            </label>
          </div>
          {hasWebsiteLinks && <OnboardingTrainedLinksTable />}
        </div>

        <div
          className={activeTab === "uploadFiles" ? "block w-full" : "hidden"}
        >
          <UploadFilesStep restartPolling={restartPolling} />
        </div>

        <div className={activeTab === "addText" ? "block w-full" : "hidden"}>
          <AddTextStep />
        </div>

        <div
          className={activeTab === "connectApps" ? "block w-full" : "hidden"}
        >
          <ConnectAppsStep />
        </div>
      </motion.div>

      <AnimatePresence>
        {isBackgroundTrainingActive && !isTrainingBannerDismissed && (
          <BackgroundTrainingBanner
            message={backgroundTrainingMessage}
            onClose={() => setIsTrainingBannerDismissed(true)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default TrainBotStep;

const TrainingInfoIcon = () => (
  <svg
    width={12}
    height={12}
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="6" cy="6" r="5.5" stroke="#6498EA" />
    <path
      d="M6 5.25V8"
      stroke="#6498EA"
      strokeWidth="1.1"
      strokeLinecap="round"
    />
    <circle cx="6" cy="3.45" r="0.65" fill="#6498EA" />
  </svg>
);

const TrainingBannerCloseIcon = () => (
  <svg
    width={12}
    height={12}
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 3L3 9"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinecap="round"
    />
    <path
      d="M3 3L9 9"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinecap="round"
    />
  </svg>
);
